// src/contexts/WebsiteBuilderContext.js
import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo } from 'react';
import * as firestoreService from '../services/firestore-service';
import { auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { isOldModalityFormat, migrateModalities } from '../utils/migrateModalities';

// ============================================================================
// UTILITY: localStorage cleanup for quota management
// ============================================================================

const checkLocalStorageSize = () => {
  let total = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      total += localStorage[key].length + key.length;
    }
  }
  return total;
};

const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

export const cleanupOversizedData = () => {
  const sizeBefore = checkLocalStorageSize();
  console.log(`📊 localStorage size before cleanup: ${formatBytes(sizeBefore)}`);

  // Check each key for base64 images (data:image/)
  const keysToClean = ['practitioner_website_draft', 'practitioner_website_published'];
  let cleanedCount = 0;

  keysToClean.forEach(key => {
    try {
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        let hasChanges = false;

        // Check hero custom image
        if (parsed.hero?.customImageUrl && parsed.hero.customImageUrl.startsWith('data:image/')) {
          console.log(`🧹 Removing base64 hero image from ${key}`);
          parsed.hero.customImageUrl = null;
          hasChanges = true;
          cleanedCount++;
        }

        // Check logo data
        if (parsed.contactFooter?.logo?.data && typeof parsed.contactFooter.logo.data === 'string' && parsed.contactFooter.logo.data.startsWith('data:image/')) {
          console.log(`🧹 Removing base64 logo from ${key}`);
          parsed.contactFooter.logo.data = null;
          hasChanges = true;
          cleanedCount++;
        }

        // Check gallery images
        if (parsed.content?.gallery?.images && Array.isArray(parsed.content.gallery.images)) {
          const originalLength = parsed.content.gallery.images.length;
          parsed.content.gallery.images = parsed.content.gallery.images.filter(img => {
            if (img.imageData && img.imageData.startsWith('data:image/')) {
              console.log(`🧹 Removing base64 gallery image from ${key}`);
              cleanedCount++;
              return false;
            }
            return true;
          });
          if (parsed.content.gallery.images.length !== originalLength) {
            hasChanges = true;
          }
        }

        // Check testimonials images (if any)
        if (parsed.content?.testimonials && Array.isArray(parsed.content.testimonials)) {
          parsed.content.testimonials = parsed.content.testimonials.filter(t => {
            if (t.image && t.image.startsWith('data:image/')) {
              console.log(`🧹 Removing base64 testimonial image from ${key}`);
              cleanedCount++;
              return false;
            }
            return true;
          });
        }

        if (hasChanges) {
          localStorage.setItem(key, JSON.stringify(parsed));
          console.log(`✅ Cleaned ${key}`);
        }
      }
    } catch (error) {
      console.error(`❌ Error cleaning ${key}:`, error);
    }
  });

  const sizeAfter = checkLocalStorageSize();
  const saved = sizeBefore - sizeAfter;

  console.log(`📊 localStorage size after cleanup: ${formatBytes(sizeAfter)}`);
  console.log(`💾 Saved: ${formatBytes(saved)}`);
  console.log(`🧹 Cleaned ${cleanedCount} base64 images`);

  return {
    sizeBefore,
    sizeAfter,
    saved,
    cleanedCount
  };
};

// ============================================================================
// INITIAL STATE
// ============================================================================

const getInitialState = () => ({
  draft: {
    design: {
      colorScheme: 'slate-silver',
      customColors: null,
      fontStyle: 'classic',
      layoutStyle: 'warm',
      sectionStyle: 'divided', // 'clean' | 'divided' | 'alternating'
      buttonStyle: {
        cornerRadius: 'rounded',
        size: 'medium'
      },
      sectionBackgrounds: {
        enabled: true,
        alternateColors: true
      },
      scrollAnimation: 'fadeIn' // 'fadeIn' | 'slideUp' | 'scale' | 'none'
    },
    hero: {
      type: 'color',
      heroColor: null,
      gradientStartColor: null,
      gradientEndColor: null,
      gradientDirection: 'to bottom',
      imageId: null,
      imageUrl: null,
      imageAttribution: null,
      imageAttributionUrl: null,
      customImageUrl: null,
      overlay: 'none',
      textAlign: 'center',
      height: 'medium',
      imageVerticalPosition: 'center',
      imageRepositionOffset: { x: 0, y: 0 },
      headline: '',
      subheadline: ''
    },
    content: {
      about: {
        bio: '',
        extendedAbout: ''
      },
      services: [],
      faqs: [],
      testimonials: [],
      gallery: {
        images: [],
        layout: 'justified'
      }
    },
    contactFooter: {
      email: '',
      phone: '',
      showEmail: true,
      showPhone: true,
      address: {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      showAddress: false,
      showContactForm: false,
      sectionTitle: 'Get in Touch',
      socialLinks: {
        instagram: '',
        facebook: '',
        tiktok: '',
        youtube: '',
        twitter: '',
        linkedin: '',
        pinterest: ''
      },
      socialIconStyle: 'filled',
      socialDisplayLocation: 'both',
      logo: {
        type: null,
        data: null,
        fontStyle: null
      },
      cancellationPolicy: null,
      footerConfig: {
        copyrightName: '',
        layout: 'simple', // 'simple' | 'detailed'
        showCancellationPolicy: true,
        showPrivacyPolicy: true,
        showTermsOfService: true,
        showPoweredBy: true
      }
    },
    marketing: {
      newsletter: {
        enabled: false,
        headline: 'Stay Connected',
        description: 'Sign up to receive updates on new offerings, insights, and upcoming events.',
        layout: 'centered-box',
        showNameField: true
      }
    },
    profile: {
      fullName: '',
      displayName: '',
      photo: null,
      motivation: '',
      modalities: { selected: [], custom: [], parentCategories: [] }
    },
    // Section management (Phase C)
    sectionOrder: ['hero', 'about', 'services', 'faqs', 'testimonials', 'gallery', 'newsletter', 'footer'],
    sectionVisibility: {
      hero: true,         // Always visible (pinned)
      about: true,
      services: true,
      faqs: true,
      testimonials: true,
      gallery: true,
      newsletter: false,  // Defaults to disabled per specs
      footer: true        // Always visible (pinned)
    }
  },
  published: null,
  isDirty: false,
  lastPublished: null,
  lastAutoSave: null,
  saveStatus: null,  // null | 'saving' | 'saved' | 'error'
  isLoading: true    // Loading state for Firestore fetch
});

// ============================================================================
// ACTION TYPES
// ============================================================================

const ActionTypes = {
  UPDATE_DESIGN: 'UPDATE_DESIGN',
  UPDATE_HERO: 'UPDATE_HERO',
  UPDATE_CONTENT: 'UPDATE_CONTENT',
  UPDATE_CONTACT_FOOTER: 'UPDATE_CONTACT_FOOTER',
  UPDATE_MARKETING: 'UPDATE_MARKETING',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  UPDATE_MULTIPLE: 'UPDATE_MULTIPLE',
  REORDER_SECTIONS: 'REORDER_SECTIONS',
  TOGGLE_SECTION_VISIBILITY: 'TOGGLE_SECTION_VISIBILITY',
  PUBLISH: 'PUBLISH',
  DISCARD: 'DISCARD',
  AUTO_SAVE: 'AUTO_SAVE',
  UPDATE_SAVE_STATUS: 'UPDATE_SAVE_STATUS',
  SET_LOADING: 'SET_LOADING',
  LOAD_STATE: 'LOAD_STATE',
  RESET: 'RESET'
};

// ============================================================================
// REDUCER
// ============================================================================

const websiteBuilderReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.UPDATE_DESIGN: {
      return {
        ...state,
        draft: {
          ...state.draft,
          design: {
            ...state.draft.design,
            [action.field]: action.value
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_HERO: {
      return {
        ...state,
        draft: {
          ...state.draft,
          hero: {
            ...state.draft.hero,
            [action.field]: action.value
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_CONTENT: {
      return {
        ...state,
        draft: {
          ...state.draft,
          content: {
            ...state.draft.content,
            [action.contentType]: action.data
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_CONTACT_FOOTER: {
      return {
        ...state,
        draft: {
          ...state.draft,
          contactFooter: {
            ...state.draft.contactFooter,
            [action.field]: action.value
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_MARKETING: {
      return {
        ...state,
        draft: {
          ...state.draft,
          marketing: {
            ...state.draft.marketing,
            [action.field]: action.value
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_PROFILE: {
      return {
        ...state,
        draft: {
          ...state.draft,
          profile: {
            ...state.draft.profile,
            [action.field]: action.value
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.UPDATE_MULTIPLE: {
      let newDraft = { ...state.draft };

      // Apply all updates from the updates object
      Object.keys(action.updates).forEach(section => {
        if (newDraft[section]) {
          newDraft[section] = {
            ...newDraft[section],
            ...action.updates[section]
          };
        }
      });

      return {
        ...state,
        draft: newDraft,
        isDirty: true
      };
    }

    case ActionTypes.REORDER_SECTIONS: {
      return {
        ...state,
        draft: {
          ...state.draft,
          sectionOrder: action.newOrder
        },
        isDirty: true
      };
    }

    case ActionTypes.TOGGLE_SECTION_VISIBILITY: {
      return {
        ...state,
        draft: {
          ...state.draft,
          sectionVisibility: {
            ...state.draft.sectionVisibility,
            [action.sectionId]: !state.draft.sectionVisibility[action.sectionId]
          }
        },
        isDirty: true
      };
    }

    case ActionTypes.PUBLISH: {
      const now = new Date();
      return {
        ...state,
        published: JSON.parse(JSON.stringify(state.draft)), // Deep clone
        isDirty: false,
        lastPublished: now
      };
    }

    case ActionTypes.DISCARD: {
      return {
        ...state,
        draft: state.published ? JSON.parse(JSON.stringify(state.published)) : state.draft,
        isDirty: false
      };
    }

    case ActionTypes.AUTO_SAVE: {
      return {
        ...state,
        lastAutoSave: new Date()
      };
    }

    case ActionTypes.UPDATE_SAVE_STATUS: {
      return {
        ...state,
        saveStatus: action.status
      };
    }

    case ActionTypes.SET_LOADING: {
      return {
        ...state,
        isLoading: action.isLoading
      };
    }

    case ActionTypes.LOAD_STATE: {
      return {
        ...state,
        ...action.state,
        isLoading: false
      };
    }

    case ActionTypes.RESET: {
      return getInitialState();
    }

    default:
      return state;
  }
};

// ============================================================================
// CONTEXT
// ============================================================================

const WebsiteBuilderContext = createContext(null);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const WebsiteBuilderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(websiteBuilderReducer, getInitialState());
  const autoSaveTimeoutRef = useRef(null);
  const clearStatusTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const firestoreSyncedRef = useRef(false); // Tracks whether initial Firestore sync has completed

  // Mirror state.draft into a ref so publish/autoSave can read the latest value
  // at invocation time without taking it as a useCallback dependency. This keeps
  // their identities stable across renders, which in turn keeps the wrapping
  // `actions` useMemo (PR #15) from re-memoizing on every keystroke. See DEF-037.
  const latestDraftRef = useRef(state.draft);
  useEffect(() => {
    latestDraftRef.current = state.draft;
  }, [state.draft]);

  // ============================================================================
  // FIRESTORE INITIALIZATION - Load from Firestore on mount
  // ============================================================================

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initializeState = async () => {
      try {
        console.log('🔄 Loading website data...');

        // =====================================================
        // PRIORITY 1: Read from localStorage (no auth required)
        // =====================================================
        const localDraftStr = localStorage.getItem('practitioner_website_draft');
        const localPublishedStr = localStorage.getItem('practitioner_website_published');
        const localLastPublished = localStorage.getItem('practitioner_website_last_published');

        if (localDraftStr) {
          console.log('✅ Found data in localStorage');

          let draft = null;
          let published = null;

          try {
            draft = JSON.parse(localDraftStr);
          } catch (e) {
            console.warn('Failed to parse local draft:', e);
          }

          if (localPublishedStr) {
            try {
              published = JSON.parse(localPublishedStr);
            } catch (e) {
              console.warn('Failed to parse local published:', e);
            }
          }

          if (draft) {
            // Merge gallery images from separate localStorage keys
            const galleryDraftStr = localStorage.getItem('practitioner_gallery_draft');
            const galleryLayout = localStorage.getItem('practitioner_gallery_layout');

            if (galleryDraftStr && draft.content?.gallery) {
              try {
                draft.content.gallery.images = JSON.parse(galleryDraftStr);
              } catch (e) { /* ignore */ }
            }
            if (galleryLayout && draft.content?.gallery) {
              draft.content.gallery.layout = galleryLayout;
            }

            if (published) {
              const galleryPublishedStr = localStorage.getItem('practitioner_gallery_published');
              if (galleryPublishedStr && published.content?.gallery) {
                try {
                  published.content.gallery.images = JSON.parse(galleryPublishedStr);
                } catch (e) { /* ignore */ }
              }
            }

            // If a published version exists and the draft differs, restore the dirty flag
            // so the status bar correctly shows "Draft (unpublished changes)" after refresh.
            const hasDraftChanges = published !== null && JSON.stringify(draft) !== JSON.stringify(published);

            // deepMerge with default schema so stale localStorage (predating any schema
            // addition such as sectionVisibility) gets missing fields filled in. Mirrors
            // the Firestore path below.
            const defaultState = getInitialState().draft;
            const mergedDraft = draft ? deepMerge(defaultState, draft) : draft;
            const mergedPublished = published ? deepMerge(defaultState, published) : published;

            dispatch({
              type: ActionTypes.LOAD_STATE,
              state: {
                draft: mergedDraft,
                published: mergedPublished,
                isDirty: hasDraftChanges,
                lastPublished: localLastPublished ? new Date(localLastPublished) : null,
                lastAutoSave: null,
              },
            });

            // Background sync to Firestore (needs userId — skipped if auth not yet resolved)
            const userId = firestoreService.getCurrentUserId();
            if (userId) {
              try {
                const cleanDraft = prepareForFirestore(draft);
                await firestoreService.saveWebsiteDraft(userId, cleanDraft);
                if (published) {
                  const cleanPublished = prepareForFirestore(published);
                  console.log('🔄 BG SYNC: Writing website_published to Firestore from localStorage');
                  await firestoreService.saveWebsitePublished(userId, cleanPublished);
                  console.log('✅ BG SYNC: website_published written');
                }
                firestoreSyncedRef.current = true;
                console.log('✅ Background sync to Firestore complete');
              } catch (syncError) {
                console.warn('⚠️ Background Firestore sync failed (non-critical):', syncError);
              }
            } else {
              console.log('ℹ️ No userId yet — Firestore sync deferred to auth listener');
            }

            return;
          }
        }

        // For Priority 2+, Firestore access requires a userId
        const userId = firestoreService.getCurrentUserId();
        if (!userId) {
          console.log('⚠️ No user ID and no localStorage data - using default state');
          dispatch({ type: ActionTypes.SET_LOADING, isLoading: false });
          return;
        }

        // =====================================================
        // PRIORITY 2: Read from Firestore (cross-device sync)
        // =====================================================
        console.log('ℹ️ No localStorage data - checking Firestore...');

        const firestoreDraft = await firestoreService.getWebsiteDraft(userId);
        const firestorePublished = await firestoreService.getWebsitePublished(userId);
        const firestoreLastPublished = await firestoreService.getWebsiteLastPublished(userId);

        if (firestoreDraft && Object.keys(firestoreDraft).length > 0) {
          console.log('✅ Loaded data from Firestore');

          const defaultState = getInitialState().draft;
          const draft = deepMerge(defaultState, firestoreDraft);
          const published = firestorePublished ? deepMerge(defaultState, firestorePublished) : null;

          // Merge gallery from localStorage
          const galleryDraftStr = localStorage.getItem('practitioner_gallery_draft');
          if (galleryDraftStr && draft.content?.gallery) {
            try {
              draft.content.gallery.images = JSON.parse(galleryDraftStr);
            } catch (e) { /* ignore */ }
          }

          // Save to localStorage for next time
          try {
            localStorage.setItem('practitioner_website_draft', JSON.stringify(draft));
            if (published) {
              localStorage.setItem('practitioner_website_published', JSON.stringify(published));
            }
          } catch (e) {
            console.warn('⚠️ Failed to cache to localStorage:', e);
          }

          const hasDraftChanges = published !== null && JSON.stringify(draft) !== JSON.stringify(published);

          dispatch({
            type: ActionTypes.LOAD_STATE,
            state: {
              draft,
              published,
              isDirty: hasDraftChanges,
              lastPublished: firestoreLastPublished,
              lastAutoSave: null,
            },
          });
          return;
        }

        // =====================================================
        // PRIORITY 3: Migrate from old localStorage keys
        // =====================================================
        console.log('ℹ️ No Firestore data - checking old localStorage keys...');

        const cleanup = cleanupOversizedData();
        if (cleanup.cleanedCount > 0) {
          console.log(`✅ Cleaned up ${cleanup.cleanedCount} base64 images`);
        }

        const migratedDraft = migrateFromOldKeys();

        if (migratedDraft) {
          console.log('✅ Migrated from old localStorage keys');

          // Save to new format
          try {
            localStorage.setItem('practitioner_website_draft', JSON.stringify(migratedDraft));
            localStorage.setItem('practitioner_website_published', JSON.stringify(migratedDraft));
            localStorage.setItem('practitioner_website_last_published', new Date().toISOString());
          } catch (e) {
            console.warn('⚠️ Failed to save migrated data:', e);
          }

          // Sync to Firestore
          try {
            await firestoreService.migrateLocalStorageToFirestore(userId);
          } catch (e) {
            console.warn('⚠️ Firestore migration failed (non-critical):', e);
          }

          dispatch({
            type: ActionTypes.LOAD_STATE,
            state: {
              draft: migratedDraft,
              published: JSON.parse(JSON.stringify(migratedDraft)),
              isDirty: false,
              lastPublished: new Date(),
              lastAutoSave: null,
            },
          });
          return;
        }

        // =====================================================
        // PRIORITY 4: Use defaults (brand new account)
        // =====================================================
        console.log('ℹ️ No existing data found - using initial defaults');
        dispatch({ type: ActionTypes.SET_LOADING, isLoading: false });

      } catch (error) {
        console.error('❌ Initialization error:', error);
        dispatch({ type: ActionTypes.SET_LOADING, isLoading: false });
      }
    };

    initializeState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // BASE64 DETECTOR UTILITY
  // ============================================================================

  const detectBase64InState = (obj, path = '') => {
    const base64Found = [];

    const scan = (value, currentPath) => {
      if (typeof value === 'string' && value.startsWith('data:image/')) {
        base64Found.push({
          path: currentPath,
          size: value.length,
          preview: value.substring(0, 50) + '...'
        });
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => scan(item, `${currentPath}[${index}]`));
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(key => scan(value[key], currentPath ? `${currentPath}.${key}` : key));
      }
    };

    scan(obj, path);
    return base64Found;
  };

  // ============================================================================
  // FIRESTORE PREPARATION UTILITY
  // ============================================================================

  /**
   * Prepare draft data for Firestore by removing large Base64 image data.
   * Only strips gallery images (Base64) which are saved separately in localStorage.
   * Keeps hero image URLs (they're Unsplash URLs, not Base64).
   * Keeps service images for now (they're small enough for Firestore).
   *
   * @param {object} draftData - The draft state to prepare
   * @returns {object} - Cleaned draft ready for Firestore
   */
  /**
   * Deep merge two objects, with source overwriting target
   * Used to merge partial Firestore data with full default state
   */
  const deepMerge = (target, source) => {
    if (!source) return target;
    if (!target) return source;

    const result = { ...target };

    Object.keys(source).forEach(key => {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    });

    return result;
  };

  const prepareForFirestore = (draftData) => {
    if (!draftData) return draftData;

    // Deep clone to avoid mutating original
    const cleaned = JSON.parse(JSON.stringify(draftData));

    // Helper: is this a Base64 data URI of any type?
    const isBase64 = (v) =>
      typeof v === 'string' &&
      (v.startsWith('data:image/') || v.startsWith('data:application/'));

    // ── Hero ─────────────────────────────────────────────────────────────────
    if (isBase64(cleaned.hero?.customImageUrl)) {
      console.log('⚠️ FIRESTORE PREP: hero.customImageUrl is Base64 — stripped');
      cleaned.hero.customImageUrl = null;
    }
    if (isBase64(cleaned.hero?.backgroundImage)) {
      console.log('⚠️ FIRESTORE PREP: hero.backgroundImage is Base64 — stripped');
      cleaned.hero.backgroundImage = null;
    }

    // ── Logo ─────────────────────────────────────────────────────────────────
    if (cleaned.contactFooter?.logo) {
      const logo = cleaned.contactFooter.logo;
      ['data', 'uploadedImage', 'src', 'url', 'imageData'].forEach(f => {
        if (isBase64(logo[f])) {
          console.log(`⚠️ FIRESTORE PREP: contactFooter.logo.${f} is Base64 — stripped`);
          logo[f] = null;
        }
      });
    }

    // ── Profile photo ─────────────────────────────────────────────────────────
    if (cleaned.profile) {
      ['photo', 'photoUrl', 'image', 'avatar'].forEach(f => {
        if (isBase64(cleaned.profile[f])) {
          console.log(`⚠️ FIRESTORE PREP: profile.${f} is Base64 — stripped`);
          cleaned.profile[f] = null;
        }
      });
    }

    // ── Gallery images ────────────────────────────────────────────────────────
    const stripBase64Gallery = (images) => {
      if (!Array.isArray(images) || images.length === 0) return images;
      const hasBase64 = images.some(img =>
        ['imageUrl', 'thumbnail', 'image', 'src', 'url', 'data'].some(f => isBase64(img[f]))
      );
      if (hasBase64) {
        console.log('⚠️ FIRESTORE PREP: Gallery has Base64 images — stripped (Storage URLs only in Firestore)');
        return [];
      }
      return images;
    };
    if (Array.isArray(cleaned.content?.gallery)) {
      cleaned.content.gallery = stripBase64Gallery(cleaned.content.gallery);
    }
    if (cleaned.content?.gallery?.images) {
      cleaned.content.gallery.images = stripBase64Gallery(cleaned.content.gallery.images);
    }

    // ── Services ─────────────────────────────────────────────────────────────
    if (Array.isArray(cleaned.content?.services)) {
      cleaned.content.services = cleaned.content.services.map(service => {
        const updated = { ...service };
        ['image', 'serviceImage', 'photo', 'thumbnail', 'src', 'url', 'imageData'].forEach(f => {
          if (isBase64(service[f])) {
            console.log(`⚠️ FIRESTORE PREP: service[${service.name}].${f} is Base64 — stripped`);
            updated[f] = null;
          }
        });
        return updated;
      });
    }

    // ── Testimonials ──────────────────────────────────────────────────────────
    if (Array.isArray(cleaned.content?.testimonials)) {
      cleaned.content.testimonials = cleaned.content.testimonials.map(t => {
        const updated = { ...t };
        ['photo', 'image', 'avatar', 'photoUrl'].forEach(f => {
          if (isBase64(t[f])) {
            console.log(`⚠️ FIRESTORE PREP: testimonial.${f} is Base64 — stripped`);
            updated[f] = null;
          }
        });
        return updated;
      });
    }

    // ── Recursive safety net ──────────────────────────────────────────────────
    // Catches any field not explicitly handled above.
    // Threshold: 500 chars — a legitimate URL is never this long.
    const stripBase64Recursive = (obj, path = '') => {
      if (obj === null || obj === undefined) return obj;
      if (Array.isArray(obj)) {
        return obj.map((item, i) => stripBase64Recursive(item, `${path}[${i}]`));
      }
      if (typeof obj === 'object') {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
          result[key] = stripBase64Recursive(value, path ? `${path}.${key}` : key);
        }
        return result;
      }
      if (typeof obj === 'string' && obj.length > 500 &&
          (obj.startsWith('data:image/') || obj.startsWith('data:application/'))) {
        console.warn(`⚠️ FIRESTORE PREP SAFETY NET: Base64 at '${path}' (${Math.round(obj.length / 1024)}KB) — stripped. Add explicit handling for this field.`);
        return null;
      }
      return obj;
    };

    const result = stripBase64Recursive(cleaned);

    // ── Size check ────────────────────────────────────────────────────────────
    const sizeBytes = JSON.stringify(result).length;
    console.log(`prepareForFirestore: total payload size: ${sizeBytes} bytes (${Math.round(sizeBytes / 1024)}KB)`);
    if (sizeBytes > 500 * 1024) {
      console.warn(`⚠️ prepareForFirestore: payload is ${Math.round(sizeBytes / 1024)}KB — expected under 100KB with no Base64 data. Something may still be large.`);
    }

    return result;
  };

  // ============================================================================
  // AUTO-SAVE: localStorage (immediate) + Firestore (debounced 1000ms)
  // ============================================================================

  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (state.isLoading) return; // Don't auto-save during initial load

    // ═══════════════════════════════════════════════════════════════════════════
    // IMMEDIATE: Write to localStorage on every state change (no debounce)
    // localStorage is synchronous — data must survive quick page refreshes.
    // The debounce timeout below only gates the slower Firestore write.
    // ═══════════════════════════════════════════════════════════════════════════
    if (state.isDirty) {
      try {
        localStorage.setItem('practitioner_website_draft', JSON.stringify(state.draft));

        // Gallery: flat array (new GalleryPanel format) or object wrapper (legacy)
        if (Array.isArray(state.draft.content?.gallery)) {
          localStorage.setItem('practitioner_gallery_draft', JSON.stringify(state.draft.content.gallery));
        } else if (state.draft.content?.gallery?.images) {
          localStorage.setItem('practitioner_gallery_draft', JSON.stringify(state.draft.content.gallery.images));
          localStorage.setItem('practitioner_gallery_layout', state.draft.content.gallery.layout || 'justified');
        }
      } catch (storageError) {
        if (storageError.name === 'QuotaExceededError') {
          console.warn('⚠️ localStorage quota exceeded — draft not saved locally');
        } else {
          console.warn('⚠️ localStorage write failed:', storageError.message);
        }
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // DEBOUNCED: Firestore sync after 1000ms of inactivity
    // Cancelled on re-renders (user still typing) and on unmount.
    // localStorage was already written above so data is safe regardless.
    // ═══════════════════════════════════════════════════════════════════════════
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (state.isDirty) {
        dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: 'saving' });

        try {
          // SAFETY CHECK: Scan for base64 before Firestore write
          const base64Found = detectBase64InState(state.draft);
          if (base64Found.length > 0) {
            console.warn('⚠️ BASE64 DETECTED IN STATE - THIS SHOULD BE A URL!');
            console.warn('Base64 images found at:', base64Found);
            console.warn('Total base64 size:', base64Found.reduce((sum, item) => sum + item.size, 0), 'bytes');
            console.warn('Locations:', base64Found.map(item => `- ${item.path} (${Math.round(item.size / 1024)}KB)`).join('\n'));
          }

          const userId = firestoreService.getCurrentUserId();

          if (!userId) {
            // localStorage already written above — just update UI status
            console.log('ℹ️ No user ID - draft saved to localStorage (Firestore sync deferred)');
            dispatch({ type: ActionTypes.AUTO_SAVE });
            dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: 'saved' });
            if (clearStatusTimeoutRef.current) {
              clearTimeout(clearStatusTimeoutRef.current);
            }
            clearStatusTimeoutRef.current = setTimeout(() => {
              dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: null });
            }, 3000);
            return;
          }

          // Prepare data for Firestore (strip Base64 gallery images to stay under 1MB limit)
          const cleanedDraft = prepareForFirestore(state.draft);

          // Save to Firestore
          await firestoreService.saveWebsiteDraft(userId, cleanedDraft);
          dispatch({ type: ActionTypes.AUTO_SAVE });
          dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: 'saved' });

          if (clearStatusTimeoutRef.current) {
            clearTimeout(clearStatusTimeoutRef.current);
          }
          clearStatusTimeoutRef.current = setTimeout(() => {
            dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: null });
          }, 3000);

        } catch (error) {
          dispatch({ type: ActionTypes.UPDATE_SAVE_STATUS, status: 'error' });
          console.error('❌ Firestore auto-save error:', error);
          if (error.code === 'permission-denied') {
            console.error('Permission denied - check Firestore rules');
          } else if (error.code === 'unavailable') {
            console.error('Firestore unavailable - check network connection');
          }
        }
      }
    }, 1000);

    // Only the Firestore timeout is cancelled on cleanup.
    // localStorage was already written synchronously above.
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (clearStatusTimeoutRef.current) {
        clearTimeout(clearStatusTimeoutRef.current);
      }
    };
  }, [state.draft, state.isDirty, state.isLoading]);

  // ============================================================================
  // AUTH STATE LISTENER - Deferred Firestore sync when auth resolves
  // ============================================================================
  // Firebase Auth restores sessions asynchronously (~300-500ms after mount).
  // If initializeState ran before auth resolved, Firestore sync was skipped.
  // This listener catches that case and syncs once auth becomes available.

  useEffect(() => {
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !firestoreSyncedRef.current) {
        firestoreSyncedRef.current = true;

        // Expose console helper so the existing bloated Firestore document
        // can be cleaned on demand without going through the full publish flow.
        window.cleanFirestoreDocument = async () => {
          console.log('🧹 cleanFirestoreDocument: starting cleanup for user', user.uid);
          try {
            const { db } = await import('../config/firebase-config');
            const { doc, getDoc } = await import('firebase/firestore');
            const docRef = doc(db, 'practitioners', user.uid);
            const snap = await getDoc(docRef);
            if (!snap.exists()) { console.warn('cleanFirestoreDocument: no document found'); return; }
            const data = snap.data();
            const beforeSize = JSON.stringify(data).length;
            console.log(`cleanFirestoreDocument: current document size ${beforeSize} bytes (${Math.round(beforeSize / 1024)}KB)`);

            const cleanDraft = data.website_draft ? prepareForFirestore(data.website_draft) : null;
            const cleanPublished = data.website_published ? prepareForFirestore(data.website_published) : null;

            if (cleanDraft || cleanPublished) {
              await firestoreService.saveWebsitePublished(user.uid, cleanPublished || cleanDraft);
              console.log('✅ cleanFirestoreDocument: wrote clean website_draft + website_published');
            } else {
              console.log('cleanFirestoreDocument: nothing to clean');
            }
          } catch (err) {
            console.error('cleanFirestoreDocument failed:', err);
          }
        };
        console.log('ℹ️ window.cleanFirestoreDocument() available — run in console to nuke old Base64 data from Firestore');

        const localDraftStr = localStorage.getItem('practitioner_website_draft');
        if (!localDraftStr) return;

        try {
          const draft = JSON.parse(localDraftStr);
          const cleanDraft = prepareForFirestore(draft);
          await firestoreService.saveWebsiteDraft(user.uid, cleanDraft);

          const localPublishedStr = localStorage.getItem('practitioner_website_published');
          if (localPublishedStr) {
            const published = JSON.parse(localPublishedStr);
            const cleanPublished = prepareForFirestore(published);
            console.log('🔄 AUTH SYNC: Writing website_published to Firestore from localStorage');
            await firestoreService.saveWebsitePublished(user.uid, cleanPublished);
            console.log('✅ AUTH SYNC: website_published written');
          }

          console.log('✅ Deferred Firestore sync complete (auth resolved)');
        } catch (error) {
          console.warn('⚠️ Deferred Firestore sync failed (non-critical):', error);
        }
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // ACTIONS
  // ============================================================================

  // Each action is hoisted as a stable useCallback so it can be referenced
  // as a dependency of the wrapping `actions` useMemo below. See DEF-035 +
  // docs/CLAUDE.md "Provider stability requirements" for the rationale.

  const updateDesign = useCallback((field, value) => {
    dispatch({ type: ActionTypes.UPDATE_DESIGN, field, value });
  }, []);

  const updateHero = useCallback((field, value) => {
    dispatch({ type: ActionTypes.UPDATE_HERO, field, value });
  }, []);

  const updateContent = useCallback((contentType, data) => {
    dispatch({ type: ActionTypes.UPDATE_CONTENT, contentType, data });
  }, []);

  const updateContactFooter = useCallback((field, value) => {
    dispatch({ type: ActionTypes.UPDATE_CONTACT_FOOTER, field, value });
  }, []);

  const updateMarketing = useCallback((field, value) => {
    dispatch({ type: ActionTypes.UPDATE_MARKETING, field, value });
  }, []);

  const updateProfile = useCallback((field, value) => {
    dispatch({ type: ActionTypes.UPDATE_PROFILE, field, value });
  }, []);

  const updateMultiple = useCallback((updates) => {
    dispatch({ type: ActionTypes.UPDATE_MULTIPLE, updates });
  }, []);

  const reorderSections = useCallback((newOrder) => {
    // Validate that Hero is first and Footer is last
    if (newOrder[0] !== 'hero') {
      console.warn('Hero must be first section');
      return;
    }
    if (newOrder[newOrder.length - 1] !== 'footer') {
      console.warn('Footer must be last section');
      return;
    }

    dispatch({ type: ActionTypes.REORDER_SECTIONS, newOrder });
  }, []);

  const toggleSectionVisibility = useCallback((sectionId) => {
    // Prevent toggling Hero and Footer (always visible)
    if (sectionId === 'hero' || sectionId === 'footer') {
      console.warn(`${sectionId} visibility cannot be toggled`);
      return;
    }

    dispatch({ type: ActionTypes.TOGGLE_SECTION_VISIBILITY, sectionId });
  }, []);

  const publish = useCallback(async () => {
    const draft = latestDraftRef.current;
    console.log('🚀 PUBLISH: Starting publish...');

    // Hard check: need userId to know which Firestore document to write.
    // This is the only condition that prevents the local publish from happening.
    const userId = firestoreService.getCurrentUserId();
    console.log('🚀 PUBLISH: userId =', userId, '| auth.currentUser =', auth?.currentUser?.uid ?? 'null');

    if (!userId) {
      console.error('❌ PUBLISH: No user ID — cannot publish');
      throw new Error('User not authenticated');
    }

    // 1. Update local React state — this is what the success modal reads.
    dispatch({ type: ActionTypes.PUBLISH });

    // 2. Persist to localStorage (primary source of truth during dev).
    if (draft.content?.gallery?.images && draft.content.gallery.images.length > 0) {
      try {
        localStorage.setItem('practitioner_gallery_published', JSON.stringify(draft.content.gallery.images));
        localStorage.setItem('practitioner_gallery_layout', draft.content.gallery.layout || 'justified');
      } catch (error) {
        console.warn('⚠️ Failed to save gallery images to localStorage:', error);
      }
    }

    try {
      // Also write to draft key so initializeState (which reads draft first) loads the
      // correct content on the next page refresh after publishing.
      localStorage.setItem('practitioner_website_published', JSON.stringify(draft));
      localStorage.setItem('practitioner_website_draft', JSON.stringify(draft));
      localStorage.setItem('practitioner_website_last_published', new Date().toISOString());
    } catch (storageError) {
      console.warn('⚠️ localStorage publish save failed:', storageError);
    }

    // 3. Firestore sync — if this fails the success modal still shows, but with a warning.
    // saveWebsitePublished writes BOTH website_draft and website_published in one atomic
    // setDoc call to prevent 1MB overflow from stale large website_draft in Firestore.
    try {
      const cleanedDraft = prepareForFirestore(draft);

      // Per-field size breakdown — helps diagnose which field is too large
      const fieldSizes = Object.entries(cleanedDraft).map(
        ([k, v]) => `${k}: ${Math.round(JSON.stringify(v).length / 1024)}KB`
      );
      const totalSize = Math.round(JSON.stringify(cleanedDraft).length / 1024);
      console.log(`🚀 PUBLISH: Firestore payload ~${totalSize}KB | ${fieldSizes.join(' | ')}`);

      await firestoreService.saveWebsitePublished(userId, cleanedDraft);
      console.log('✅ PUBLISH COMPLETE — Firestore updated (website_draft + website_published)');

      // Auto-resubmit for review: if practitioner was rejected, set back to pending
      try {
        const { doc: firestoreDoc, getDoc: firestoreGet, updateDoc: firestoreUpdate } = require('firebase/firestore');
        const { db: firestoreDb } = require('../config/firebase-config');
        const practSnap = await firestoreGet(firestoreDoc(firestoreDb, 'practitioners', userId));
        if (practSnap.exists() && practSnap.data()?.verification?.manualReviewStatus === 'rejected') {
          await firestoreUpdate(firestoreDoc(firestoreDb, 'practitioners', userId), {
            'verification.manualReviewStatus': 'pending',
            'verification.rejectionFeedback': null,
          });
          console.log('🔄 PUBLISH: Auto-resubmitted for review (was rejected)');
        }
      } catch (resubmitErr) {
        console.warn('⚠️ Auto-resubmit check failed (non-blocking):', resubmitErr.message);
      }

      return true; // Signal Firestore success to caller
    } catch (firestoreError) {
      // Log full error object so the exact failure reason is visible in the console
      console.error('⚠️ PUBLISH: Firestore sync failed:', firestoreError);
      console.error('  code:', firestoreError.code, '| message:', firestoreError.message);
      return false; // Signal Firestore failure — caller will show warning in success modal
    }
  }, []);

  const discard = useCallback(() => {
    dispatch({ type: ActionTypes.DISCARD });
  }, []);

  const autoSave = useCallback(async () => {
    const draft = latestDraftRef.current;
    try {
      const userId = firestoreService.getCurrentUserId();

      if (!userId) {
        console.warn('⚠️ No user ID - cannot auto-save to Firestore');
        return;
      }

      // Save gallery images to localStorage
      if (draft.content?.gallery?.images && draft.content.gallery.images.length > 0) {
        try {
          localStorage.setItem('practitioner_gallery_draft', JSON.stringify(draft.content.gallery.images));
          localStorage.setItem('practitioner_gallery_layout', draft.content.gallery.layout || 'justified');
        } catch (error) {
          console.warn('⚠️ Failed to save gallery images to localStorage:', error);
        }
      }

      // Prepare data for Firestore (strip gallery images)
      const cleanedDraft = prepareForFirestore(draft);

      await firestoreService.saveWebsiteDraft(userId, cleanedDraft);
      dispatch({ type: ActionTypes.AUTO_SAVE });
    } catch (error) {
      console.error('❌ Manual auto-save error:', error);
    }
  }, []);

  const checkForChanges = useCallback(() => {
    return state.isDirty;
  }, [state.isDirty]);

  // Memoized actions object — reference is stable across renders that don't
  // change any underlying callback identity. Required for the latent doom-loop
  // protection documented in docs/CLAUDE.md "Provider stability requirements".
  const actions = useMemo(() => ({
    updateDesign,
    updateHero,
    updateContent,
    updateContactFooter,
    updateMarketing,
    updateProfile,
    updateMultiple,
    reorderSections,
    toggleSectionVisibility,
    publish,
    discard,
    autoSave,
    checkForChanges,
  }), [
    updateDesign,
    updateHero,
    updateContent,
    updateContactFooter,
    updateMarketing,
    updateProfile,
    updateMultiple,
    reorderSections,
    toggleSectionVisibility,
    publish,
    discard,
    autoSave,
    checkForChanges,
  ]);

  // Memoized context value — only changes when state or actions actually change.
  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <WebsiteBuilderContext.Provider value={value}>
      {children}
    </WebsiteBuilderContext.Provider>
  );
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export const useWebsiteBuilder = () => {
  const context = useContext(WebsiteBuilderContext);

  if (!context) {
    throw new Error('useWebsiteBuilder must be used within a WebsiteBuilderProvider');
  }

  return context;
};

// ============================================================================
// MODALITY NORMALIZATION HELPER
// ============================================================================

/**
 * Normalize modalities from any storage format to the new structured format.
 *   - New structured object { selected, custom, parentCategories } → returned as-is
 *   - Old flat array (category label strings from pre-taxonomy wizard) → migrated via migrateModalities
 *   - undefined/null → returns empty structured object
 */
const normalizeModalities = (raw) => {
  if (!raw) return { selected: [], custom: [], parentCategories: [] };
  if (typeof raw === 'object' && !Array.isArray(raw) && raw.selected !== undefined) {
    return {
      selected: Array.isArray(raw.selected) ? raw.selected : [],
      custom: Array.isArray(raw.custom) ? raw.custom : [],
      parentCategories: Array.isArray(raw.parentCategories) ? raw.parentCategories : [],
    };
  }
  // Old flat array (pre-taxonomy category labels) — migrate instead of discarding
  if (isOldModalityFormat(raw)) {
    return migrateModalities(raw);
  }
  return { selected: [], custom: [], parentCategories: [] };
};

// ============================================================================
// MIGRATION HELPER - Convert old localStorage keys to new unified format
// ============================================================================

const migrateFromOldKeys = () => {
  try {
    const initialState = getInitialState().draft;
    let migrated = {
      ...initialState,
      // Ensure section management fields exist
      sectionOrder: initialState.sectionOrder,
      sectionVisibility: initialState.sectionVisibility
    };
    let hasData = false;

    // Migrate wizard data
    const wizardData = localStorage.getItem('practitioner_guided_setup');
    if (wizardData) {
      const parsed = JSON.parse(wizardData);
      hasData = true;

      // Migrate website design
      if (parsed.website) {
        migrated.design = {
          colorScheme: parsed.website.colorScheme || 'lavender-gold',
          customColors: parsed.website.customColors || null,
          fontStyle: parsed.website.fontStyle || 'classic',
          layoutStyle: parsed.website.layoutStyle || 'warm',
          buttonStyle: parsed.website.buttonStyle || initialState.design.buttonStyle,
          sectionBackgrounds: parsed.website.sectionBackgrounds || initialState.design.sectionBackgrounds
        };

        // Migrate hero
        migrated.hero = {
          ...initialState.hero,
          ...parsed.website.heroSettings
        };

        // Migrate services
        if (parsed.services) {
          migrated.content.services = parsed.services;
        }
      }

      // Migrate profile
      if (parsed.profile) {
        migrated.profile = {
          fullName: parsed.profile.fullName || '',
          displayName: parsed.profile.displayName || '',
          photo: parsed.profile.photo || null,
          motivation: parsed.profile.motivation || '',
          modalities: normalizeModalities(parsed.modalities)
        };

        // Migrate social links
        if (parsed.profile.socialLinks || parsed.socialLinks) {
          migrated.contactFooter.socialLinks = parsed.profile.socialLinks || parsed.socialLinks || {};
        }
      }
    }

    // ALSO check practitioner_profile from onboarding (if wizard data didn't have profile)
    const onboardingProfile = localStorage.getItem('practitioner_profile');
    if (onboardingProfile && !migrated.profile.displayName) {
      try {
        const parsed = JSON.parse(onboardingProfile);
        hasData = true;
        migrated.profile = {
          fullName: parsed.fullName || '',
          displayName: parsed.displayName || '',
          photo: parsed.profilePhoto || parsed.photo || null,
          motivation: parsed.whyThisWork || parsed.motivation || '',
          modalities: normalizeModalities(parsed.modalities)
        };
      } catch (e) {
        console.error('Failed to parse practitioner_profile:', e);
      }
    }

    // ALSO check practitioner_setup from PublicProfile settings (if neither wizard nor onboarding had profile)
    const setupProfile = localStorage.getItem('practitioner_setup');
    if (setupProfile && !migrated.profile.displayName) {
      try {
        const parsed = JSON.parse(setupProfile);
        if (parsed.profile) {
          hasData = true;
          migrated.profile = {
            fullName: parsed.profile.fullName || '',
            displayName: parsed.profile.displayName || '',
            photo: parsed.profile.photo || null,
            motivation: parsed.profile.motivation || '',
            modalities: normalizeModalities(parsed.profile.modalities)
          };
        }
      } catch (e) {
        console.error('Failed to parse practitioner_setup:', e);
      }

      // Migrate availability to profile (not content, as it's operational)
      // Note: Availability stays in its own settings, not in website builder context
    }

    // Migrate FAQs
    const faqsDraft = localStorage.getItem('practitioner_faqs_draft');
    const faqsPublished = localStorage.getItem('practitioner_faqs_published');
    if (faqsDraft || faqsPublished) {
      hasData = true;
      migrated.content.faqs = JSON.parse(faqsDraft || faqsPublished || '[]');
    }

    // Migrate Testimonials
    const testimonialsDraft = localStorage.getItem('practitioner_testimonials_draft');
    const testimonialsPublished = localStorage.getItem('practitioner_testimonials_published');
    if (testimonialsDraft || testimonialsPublished) {
      hasData = true;
      migrated.content.testimonials = JSON.parse(testimonialsDraft || testimonialsPublished || '[]');
    }

    // Migrate Gallery
    const galleryDraft = localStorage.getItem('practitioner_gallery_draft');
    const galleryPublished = localStorage.getItem('practitioner_gallery_published');
    const galleryLayout = localStorage.getItem('practitioner_gallery_layout');
    if (galleryDraft || galleryPublished) {
      hasData = true;
      migrated.content.gallery = {
        images: JSON.parse(galleryDraft || galleryPublished || '[]'),
        layout: galleryLayout || 'justified'
      };
    }

    // Migrate design settings from WebsiteDesignSettings
    const designSettings = localStorage.getItem('practitioner_design_settings_draft');
    if (designSettings) {
      hasData = true;
      const parsed = JSON.parse(designSettings);
      if (parsed.colorScheme) migrated.design.colorScheme = parsed.colorScheme;
      if (parsed.customColors) migrated.design.customColors = parsed.customColors;
      if (parsed.fontStyle) migrated.design.fontStyle = parsed.fontStyle;
      if (parsed.layoutStyle) migrated.design.layoutStyle = parsed.layoutStyle;
      if (parsed.buttonStyle) migrated.design.buttonStyle = parsed.buttonStyle;
      if (parsed.heroSettings) migrated.hero = { ...migrated.hero, ...parsed.heroSettings };
    }

    return hasData ? migrated : null;
  } catch (error) {
    console.error('Migration error:', error);
    return null;
  }
};

export default WebsiteBuilderContext;
